/**
 * Every client's Drive folder from one place.
 *
 * The design had a Documents page that browsed one shared Drive root. The
 * studio's Drive is not arranged that way — each client has a folder, recorded
 * on the client — so this lists the clients you work on and opens the chosen
 * one's folder, subfolders and all, with the same view the client page has.
 * Which clients are listed is the server's answer, so a team member sees
 * their own.
 */

import { Folder, FolderX } from 'lucide-react'
import { useState } from 'react'
import { PageHeader } from '../components/PageHeader'
import { useAppData } from '../context/AppDataContext'
import { useAuth } from '../context/AuthContext'
import { cn } from '../lib/cn'
import { ClientDocuments } from './ClientDocuments'

export default function Documents() {
  const { currentClients } = useAppData()
  const { hasRole } = useAuth()
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<string | null>(() => {
    try {
      return localStorage.getItem('hapi.documents-client')
    } catch {
      return null
    }
  })

  const clients = [...currentClients].sort((a, b) => a.name.localeCompare(b.name))
  const q = search.trim().toLowerCase()
  const listed = q === '' ? clients : clients.filter((c) => c.name.toLowerCase().includes(q))
  const current = clients.find((c) => c.id === chosen) ?? clients[0] ?? null

  function choose(id: string) {
    setChosen(id)
    try {
      localStorage.setItem('hapi.documents-client', id)
    } catch {
      // Remembering the last folder is a convenience; without storage it opens on the first.
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        variant="dark"
        title="Documents"
        description="Each client's folder in Google Drive — briefs, scripts, cuts — without hunting for it."
      />

      {hasRole('VIEWER') ? (
        <p className="rounded-card border border-shell-border bg-shell p-6 text-sm text-shell-text">
          Client folders are for the people working on them. A viewer can see the work on each client&rsquo;s page.
        </p>
      ) : clients.length === 0 ? (
        <p className="rounded-card border border-shell-border bg-shell p-6 text-sm text-shell-text">
          You are not on any clients yet. An Admin or a manager can assign you from your page on Team.
        </p>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[15rem_minmax(0,1fr)]">
          <aside className="flex flex-col gap-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a client"
              aria-label="Find a client"
              className="field-input"
            />
            <ul className="flex gap-1 overflow-x-auto scrollbar-thin lg:flex-col lg:overflow-visible">
              {listed.map((c) => {
                const on = current?.id === c.id
                return (
                  <li key={c.id} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => choose(c.id)}
                      aria-current={on ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                        on
                          ? 'bg-brand-500/10 font-medium text-shell-text-active'
                          : 'text-shell-text hover:bg-shell-border/40'
                      )}
                    >
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: c.color }} />
                      <span className="truncate">{c.name}</span>
                    </button>
                  </li>
                )
              })}
              {listed.length === 0 && (
                <li className="flex items-center gap-2 px-3 py-2 text-sm text-shell-text/60">
                  <FolderX size={14} /> No client called that.
                </li>
              )}
            </ul>
          </aside>

          <section className="min-w-0">
            {current === null ? (
              <p className="flex items-center gap-2 text-sm text-shell-text">
                <Folder size={15} /> Choose a client.
              </p>
            ) : (
              <>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-shell-text/60">{current.name}</p>
                {/* Keyed, so going to another client starts at their top folder. */}
                <ClientDocuments key={current.id} clientId={current.id} />
              </>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
